// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the plate, the vents, and the four rules that decide whether a fitting may go somewhere.
//
// THE FOUR RULES ARE THE PRODUCT. Everything else in this game is presentation of them:
//   EMPTY   the cell is free and on the plate
//   MATCH   every side facing an OCCUPIED neighbour equals that neighbour's facing side, EXACTLY,
//           blank-against-blank included
//   MARGIN  a live side may only leave the plate through a vent of its own gauge
//   SPLICE  at least one live side meets an existing live end or a matching vent
//
// SPLICE is what makes round one have exactly twelve legal seeds — the vents — instead of 49 x 26
// arbitrary ones, and it is why the plate grows as a single frontier you are steering rather than a
// scatter of disconnected fragments. MATCH's blank-against-blank clause is the subtle one: without
// it you could park a solid wall against somebody's open end and quietly strand it forever.

import {
  BLANK,
  DX,
  DY,
  FORMS,
  live,
  matches,
  opp,
  type Dir,
  type Gauge,
  type PieceId,
  type Sides,
} from './pieces';

export const SIZE = 7;
export const CELLS = SIZE * SIZE;
/** Vents sit at these offsets along each edge. Odd indices keep them off the corners. */
export const VENT_AT: readonly number[] = [1, 3, 5];
export const VENT_COUNT = 12;

export const idx = (x: number, y: number): number => y * SIZE + x;
export const xOf = (i: number): number => i % SIZE;
export const yOf = (i: number): number => Math.floor(i / SIZE);
export const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < SIZE && y < SIZE;

/** A vent: an edge terminal of a fixed gauge that a live side may legally run out through. */
export interface Vent {
  readonly id: number;
  /** The cell just inside the plate that this vent touches. */
  readonly x: number;
  readonly y: number;
  /** The direction you travel to LEAVE the plate through it. */
  readonly dir: Dir;
  readonly gauge: Gauge;
}

/**
 * The twelve vents, identical in every game and every mode so players build knowledge of them.
 *
 * The gauge pattern is 180-degree rotationally symmetric — north reads C,F,C and south reads C,F,C
 * back along the same axis — so no corner of the plate is a better place to start than any other.
 * A vent layout that favoured one side would be a fairness bug that no amount of seat-shuffling in
 * the sim could ever surface, because every seat plays the identical plate.
 *
 * Numbered clockwise from the top-left so V0..V2 are north, V3..V5 east, V6..V8 south, V9..V11 west.
 */
export const VENTS: readonly Vent[] = buildVents();

function buildVents(): Vent[] {
  const C = 1 as Gauge;
  const F = 2 as Gauge;
  const northGauges: Gauge[] = [C, F, C];
  const eastGauges: Gauge[] = [F, C, F];
  const out: Vent[] = [];
  let id = 0;
  for (let k = 0; k < 3; k++) out.push({ id: id++, x: VENT_AT[k], y: 0, dir: 0, gauge: northGauges[k] });
  for (let k = 0; k < 3; k++) out.push({ id: id++, x: SIZE - 1, y: VENT_AT[k], dir: 1, gauge: eastGauges[k] });
  // South and west run back the other way so the numbering is genuinely clockwise.
  for (let k = 2; k >= 0; k--) out.push({ id: id++, x: VENT_AT[k], y: SIZE - 1, dir: 2, gauge: northGauges[k] });
  for (let k = 2; k >= 0; k--) out.push({ id: id++, x: 0, y: VENT_AT[k], dir: 3, gauge: eastGauges[k] });
  return out;
}

/** Vent id at a given (cell, direction) boundary, or -1. Precomputed — the legality check is hot. */
const VENT_AT_EDGE: Int8Array = (() => {
  const m = new Int8Array(CELLS * 4).fill(-1);
  for (const v of VENTS) m[idx(v.x, v.y) * 4 + v.dir] = v.id;
  return m;
})();

export const ventAt = (x: number, y: number, d: Dir): number => VENT_AT_EDGE[idx(x, y) * 4 + d];

/**
 * A drawn plate.
 *
 * `piece[i]` is -1 for an empty cell, otherwise an index into PIECE_IDS; `rot[i]` its rotation. The
 * sides are DERIVED from those two rather than stored, so there is exactly one source of truth for
 * what a cell presents and no way for a stored side table to drift out of step with the fitting.
 */
export interface Plate {
  piece: Int8Array;
  rot: Int8Array;
}

export const PIECE_ORDER: readonly PieceId[] = ['cs', 'ce', 'ct', 'fs', 'fe', 'ft', 'cp', 'bp'];
export const pieceIndexOf = (id: PieceId): number => PIECE_ORDER.indexOf(id);

export function emptyPlate(): Plate {
  return { piece: new Int8Array(CELLS).fill(-1), rot: new Int8Array(CELLS) };
}

export const occupied = (p: Plate, x: number, y: number): boolean => p.piece[idx(x, y)] >= 0;

/** The four sides a filled cell presents, or null if it is empty. */
export function sidesAt(p: Plate, x: number, y: number): Sides | null {
  const i = idx(x, y);
  const pi = p.piece[i];
  if (pi < 0) return null;
  return FORMS[PIECE_ORDER[pi]][p.rot[i]];
}

/** Why a placement was refused. `null` means it is legal. Ordered by which the player sees first. */
export type Refusal =
  | { kind: 'taken' }
  | { kind: 'match'; dir: Dir; mine: Gauge; theirs: Gauge }
  | { kind: 'margin'; dir: Dir; mine: Gauge }
  | { kind: 'splice' };

export interface PlaceOpts {
  /**
   * SEEP relaxes MATCH and MARGIN into pricing rather than refusal. A relaxed placement is legal,
   * costs, and the offending half-edges DO NOT CONDUCT — which is enforced in score.ts, not here.
   */
  readonly relaxed?: boolean;
}

/**
 * The whole legality rule, in one place, returning WHY rather than just whether.
 *
 * Returning the reason is not a nicety: the SET button's disabled state is this game's only teaching
 * surface (there is no tutorial), so "Filament meets culvert — north side" has to come from the same
 * code path that refuses the move, or the two will disagree and the game will lie to the player.
 */
export function refuse(p: Plate, s: Sides, x: number, y: number, opts: PlaceOpts = {}): Refusal | null {
  if (!inBounds(x, y) || occupied(p, x, y)) return { kind: 'taken' };

  let spliced = false;
  /**
   * A live side meeting SOMETHING — an occupied neighbour or a vent — whether or not it matches.
   *
   * SEEP splices on this rather than on `spliced`. Without it, a mismatched fitting could only be
   * placed where it ALSO matched something, so "anything fits, badly" was false: you could never
   * actually jam a piece against existing work, and the mode collapsed into Trunk with extra steps.
   */
  let touched = false;
  let firstMatch: Refusal | null = null;
  let firstMargin: Refusal | null = null;

  for (let d = 0 as Dir; d < 4; d = (d + 1) as Dir) {
    const nx = x + DX[d];
    const ny = y + DY[d];
    const mine = s[d];

    if (!inBounds(nx, ny)) {
      const v = ventAt(x, y, d);
      if (live(mine)) {
        if (v >= 0 && VENTS[v].gauge === mine) {
          spliced = true;
          touched = true;
        } else {
          if (v >= 0) touched = true;
          if (!firstMargin) firstMargin = { kind: 'margin', dir: d, mine };
        }
      }
      continue;
    }

    const theirs = sidesAt(p, nx, ny);
    if (theirs === null) continue; // an empty interior neighbour constrains nothing — that is an open end
    if (live(mine)) touched = true;
    const facing = theirs[opp(d)];
    if (!matches(mine, facing)) {
      if (!firstMatch) firstMatch = { kind: 'match', dir: d, mine, theirs: facing };
      continue;
    }
    if (live(mine)) spliced = true;
  }

  if (!opts.relaxed) {
    if (firstMatch) return firstMatch;
    if (firstMargin) return firstMargin;
  }
  // SPLICE holds in EVERY mode. Without it a plate could grow as disconnected islands and the
  // "you are steering one frontier" feeling — the entire point — would be gone.
  if (!(opts.relaxed ? touched : spliced)) return { kind: 'splice' };
  return null;
}

export const canPlace = (p: Plate, s: Sides, x: number, y: number, opts: PlaceOpts = {}): boolean =>
  refuse(p, s, x, y, opts) === null;

/**
 * Half-edges this placement would pay for in SEEP: sides that do not agree with what they meet.
 *
 * Counted here so exactly one function decides what a weep IS, and both the scorer and the audit
 * can be checked against it rather than against each other.
 */
export function weepsOf(p: Plate, s: Sides, x: number, y: number): Dir[] {
  const out: Dir[] = [];
  for (let d = 0 as Dir; d < 4; d = (d + 1) as Dir) {
    const nx = x + DX[d];
    const ny = y + DY[d];
    const mine = s[d];
    if (!inBounds(nx, ny)) {
      const v = ventAt(x, y, d);
      if (live(mine) && !(v >= 0 && VENTS[v].gauge === mine)) out.push(d);
      continue;
    }
    const theirs = sidesAt(p, nx, ny);
    if (theirs === null) continue;
    if (!matches(mine, theirs[opp(d)])) out.push(d);
  }
  return out;
}

export interface Spot {
  readonly x: number;
  readonly y: number;
  readonly rot: number;
}

/**
 * Every legal (cell, rotation) for a fitting.
 *
 * Deliberately exhaustive over 49 cells x up to 4 rotations — 196 refuse() calls, which is nothing,
 * and it is the same sweep the SCRAP rule needs to PROVE there is genuinely nowhere to go. An
 * approximate frontier-walk here would make "nowhere fits" a claim rather than a fact.
 */
export function legalSpots(p: Plate, id: PieceId, opts: PlaceOpts = {}): Spot[] {
  const forms = FORMS[id];
  const out: Spot[] = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (occupied(p, x, y)) continue;
      for (let r = 0; r < forms.length; r++) {
        if (canPlace(p, forms[r], x, y, opts)) out.push({ x, y, rot: r });
      }
    }
  }
  return out;
}

export function setCell(p: Plate, id: PieceId, rot: number, x: number, y: number): void {
  const i = idx(x, y);
  p.piece[i] = pieceIndexOf(id);
  p.rot[i] = rot;
}

export function clearCell(p: Plate, x: number, y: number): void {
  const i = idx(x, y);
  p.piece[i] = -1;
  p.rot[i] = 0;
}

export function clonePlate(p: Plate): Plate {
  return { piece: Int8Array.from(p.piece), rot: Int8Array.from(p.rot) };
}

export const filledCount = (p: Plate): number => {
  let n = 0;
  for (let i = 0; i < CELLS; i++) if (p.piece[i] >= 0) n++;
  return n;
};

/** 49 bytes: (pieceIndex + 1) << 3 | rot, so 0 is empty. This is exactly what goes on the wire. */
export function encodePlate(p: Plate): string {
  let s = '';
  for (let i = 0; i < CELLS; i++) {
    s += String.fromCharCode(p.piece[i] < 0 ? 0 : ((p.piece[i] + 1) << 3) | (p.rot[i] & 7));
  }
  return s;
}

/**
 * Decode, returning null on ANYTHING malformed.
 *
 * A rival's plate arrives over the network and is fed straight into the local scorer to render their
 * summary card, so a bad byte here would either crash the summary or paint a fitting that does not
 * exist. Rejecting the whole plate and showing "plate not received" is the honest failure.
 */
export function decodePlate(s: string): Plate | null {
  if (typeof s !== 'string' || s.length !== CELLS) return null;
  const p = emptyPlate();
  for (let i = 0; i < CELLS; i++) {
    const c = s.charCodeAt(i);
    if (c === 0) continue;
    const pi = (c >> 3) - 1;
    const r = c & 7;
    if (pi < 0 || pi >= PIECE_ORDER.length) return null;
    if (r >= FORMS[PIECE_ORDER[pi]].length) return null;
    p.piece[i] = pi;
    p.rot[i] = r;
  }
  return p;
}

export { BLANK };
